import styled from "@emotion/styled";
import { useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { AnalysisOverview } from "../../api/analysis";
import {
  DURATION_BUCKETS,
  WEEKDAYS,
  fmt1,
  fmtInt,
  fmtPct,
  n,
  per10kHours,
} from "./analysisUtil";

const FONT = "Noto Sans, Pretendard, sans-serif";

/** 기간 안의 yyyy-MM 목록 */
function monthsBetween(start: string, end: string) {
  const out: string[] = [];
  let y = Number(start.slice(0, 4));
  let m = Number(start.slice(5, 7));
  const ey = Number(end.slice(0, 4));
  const em = Number(end.slice(5, 7));
  while (y < ey || (y === ey && m <= em)) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
  }
  return out;
}

const prevYm = (ym: string) => `${Number(ym.slice(0, 4)) - 1}${ym.slice(4)}`;

// ── C. 월별 추이 ───────────────────────────────────────────────
export function MonthlyChart({ data }: { data: AnalysisOverview }) {
  const { categories, series } = useMemo(() => {
    const months = monthsBetween(data.period.start, data.period.end);
    const trips = new Map(data.monthly.map((r) => [r.ym, n(r.trips)]));
    const lyTrips = new Map(data.lastYearMonthly.map((r) => [r.ym, n(r.trips)]));
    const ev = new Map(data.monthlyEvents.map((r) => [r.ym, r]));
    return {
      categories: months.map((m) => `${m.slice(2, 4)}.${m.slice(5)}`),
      series: [
        { name: "운행 건수", type: "column", data: months.map((m) => trips.get(m) ?? 0) },
        { name: "전년 동월 운행", type: "line", data: months.map((m) => lyTrips.get(prevYm(m)) ?? 0) },
        { name: "전복", type: "line", data: months.map((m) => n(ev.get(m)?.rollover)) },
        { name: "위험운전", type: "line", data: months.map((m) => n(ev.get(m)?.danger)) },
      ],
    };
  }, [data]);

  return (
    <Chart
      type="line"
      height={320}
      series={series}
      options={{
        chart: { fontFamily: FONT, toolbar: { show: false }, zoom: { enabled: false } },
        colors: ["#25863F", "#9CA3AF", "#d7263d", "#f28c28"],
        stroke: { width: [0, 2, 3, 3], dashArray: [0, 5, 0, 0], curve: "smooth" },
        plotOptions: { bar: { columnWidth: "55%", borderRadius: 3 } },
        dataLabels: { enabled: false },
        xaxis: { categories },
        yaxis: [
          { seriesName: "운행 건수", title: { text: "운행 건수" }, labels: { formatter: (v: number) => fmtInt(v) } },
          { seriesName: "운행 건수", show: false },
          { seriesName: "전복", opposite: true, title: { text: "이벤트 건수" }, labels: { formatter: (v: number) => fmtInt(v) } },
          { seriesName: "전복", show: false },
        ],
        legend: { position: "top" },
        tooltip: { shared: true, intersect: false, y: { formatter: (v: number) => `${fmtInt(v)}건` } },
      }}
    />
  );
}

// ── C. 요일 × 시간대 ────────────────────────────────────────────
type WeekHourKind = "trips" | "rollover" | "danger";

const WEEK_HOUR_KINDS: { key: WeekHourKind; label: string; color: string }[] = [
  { key: "trips", label: "운행 시작", color: "#25863F" },
  { key: "rollover", label: "차량전복", color: "#d7263d" },
  { key: "danger", label: "위험운전", color: "#f28c28" },
];

export function WeekHourChart({ data }: { data: AnalysisOverview }) {
  const [kind, setKind] = useState<WeekHourKind>("trips");
  const meta = WEEK_HOUR_KINDS.find((k) => k.key === kind)!;
  const series = useMemo(() => {
    const value = (r: AnalysisOverview["weekHourEvents"][number]) =>
      kind === "rollover" ? n(r.rollover) : n(r.danger);
    const grid =
      kind === "trips"
        ? new Map(data.weekHour.map((r) => [`${r.dw}-${r.hr}`, n(r.n)]))
        : new Map(data.weekHourEvents.map((r) => [`${r.dw}-${r.hr}`, value(r)]));
    // ApexCharts 히트맵은 아래에서 위로 그린다. 월요일이 위에 오도록 뒤집는다.
    // 축 라벨은 숫자만 둔다. '11시' 처럼 쓰면 폭이 모자라 홀수 시간이 숨는다
    return [...WEEKDAYS].reverse().map((w) => ({
      name: w.label,
      data: Array.from({ length: 24 }, (_, h) => ({ x: String(h), y: grid.get(`${w.dw}-${h}`) ?? 0 })),
    }));
  }, [data, kind]);

  return (
    <>
      <Seg>
        {WEEK_HOUR_KINDS.map((k) => (
          <button key={k.key} type="button" aria-pressed={kind === k.key} onClick={() => setKind(k.key)}>
            {k.label}
          </button>
        ))}
      </Seg>
      <Chart
        key={kind}
        type="heatmap"
        height={300}
        series={series}
        options={{
          chart: { fontFamily: FONT, toolbar: { show: false } },
          colors: [meta.color],
          dataLabels: { enabled: false },
          xaxis: {
            labels: { rotate: 0, hideOverlappingLabels: false, style: { fontSize: "11px" } },
            title: { text: "시각 (시)", style: { fontSize: "11px", fontWeight: 400 } },
            tooltip: { enabled: false },
          },
          plotOptions: { heatmap: { radius: 2, enableShades: true, shadeIntensity: 0.6 } },
          tooltip: {
            custom: ({ seriesIndex, dataPointIndex, w }: any) => {
              const s = w.config.series[seriesIndex];
              const v = s?.data?.[dataPointIndex]?.y ?? 0;
              return `<div style="padding:6px 10px;font-size:12px">${s?.name}요일 ${dataPointIndex}시 · ${meta.label} <b>${fmtInt(v)}건</b></div>`;
            },
          },
        }}
      />
    </>
  );
}

// ── C. 운행시간 분포 ────────────────────────────────────────────
export function DurationChart({ data }: { data: AnalysisOverview }) {
  const map = new Map(data.duration.map((r) => [r.bucket, n(r.n)]));
  const buckets = DURATION_BUCKETS.filter((b) => (map.get(b.key) ?? 0) > 0 || !["odd", "open"].includes(b.key));
  return (
    <Chart
      type="bar"
      height={280}
      series={[{ name: "운행 건수", data: buckets.map((b) => map.get(b.key) ?? 0) }]}
      options={{
        chart: { fontFamily: FONT, toolbar: { show: false } },
        colors: buckets.map((b) => (["over", "odd", "open"].includes(b.key) ? "#9CA3AF" : "#25863F")),
        plotOptions: { bar: { distributed: true, borderRadius: 3, columnWidth: "60%" } },
        legend: { show: false },
        dataLabels: { enabled: false },
        xaxis: { categories: buckets.map((b) => b.label) },
        yaxis: { labels: { formatter: (v: number) => fmtInt(v) } },
        tooltip: { y: { formatter: (v: number) => `${fmtInt(v)}건` } },
      }}
    />
  );
}

// ── D. 차종별 ──────────────────────────────────────────────────
/** 운행시간이 이보다 적은 차종은 사고율을 막대에 넣지 않는다 (표본이 작아 튄다) */
const TYPE_MIN_HOURS = 500;

export function VehicleTypeSection({ data }: { data: AnalysisOverview }) {
  const rows = data.byType.filter((t) => n(t.registered) > 0 || n(t.trips) > 0);
  const rated = rows
    .filter((t) => n(t.hours) >= TYPE_MIN_HOURS)
    .map((t) => ({ name: t.name, rate: per10kHours(t.rollover, t.hours) }))
    .sort((a, b) => b.rate - a.rate);

  return (
    <TypeWrap>
      <div className="chart">
        <div className="sub">1만 운행시간당 전복 (운행 {fmtInt(TYPE_MIN_HOURS)}시간 이상 차종)</div>
        <Chart
          type="bar"
          height={Math.max(220, rated.length * 30 + 60)}
          series={[{ name: "1만 시간당 전복", data: rated.map((r) => Number(r.rate.toFixed(1))) }]}
          options={{
            chart: { fontFamily: FONT, toolbar: { show: false } },
            colors: ["#d7263d"],
            plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: "60%" } },
            dataLabels: { enabled: true, formatter: (v: number) => fmt1(v), style: { colors: ["#374151"] }, offsetX: 26 },
            xaxis: { categories: rated.map((r) => r.name) },
            tooltip: { y: { formatter: (v: number) => `${fmt1(v)}건` } },
          }}
        />
      </div>
      <div className="table">
        <table>
          <thead>
            <tr>
              <th>차종</th>
              <th>등록</th>
              <th>운행 대수</th>
              <th>운행 건수</th>
              <th>운행시간</th>
              <th>이동거리(km)</th>
              <th>전복</th>
              <th>위험운전</th>
              <th>1만 시간당 전복</th>
              <th>위험운전→전복</th>
              <th>전복 오탐</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={String(t.type_cd)}>
                <td className="l">{t.name}</td>
                <td>{fmtInt(t.registered)}</td>
                <td>{fmtInt(t.vehicles)}</td>
                <td>{fmtInt(t.trips)}</td>
                <td>{fmt1(t.hours)}</td>
                <td>{fmt1(t.km)}</td>
                <td>{fmtInt(t.rollover)}</td>
                <td>{fmtInt(t.danger)}</td>
                <td className={n(t.hours) < TYPE_MIN_HOURS ? "dim" : ""}>
                  {n(t.hours) > 0 ? fmt1(per10kHours(t.rollover, t.hours)) : "-"}
                </td>
                <td>{fmtPct(t.danger_to_rollover, t.danger)}</td>
                <td>{fmtPct(t.rollover_false, t.rollover)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="note">
          회색 사고율은 운행 {fmtInt(TYPE_MIN_HOURS)}시간 미만이라 참고만 한다. 오탐은 해제 사유가 "사고 미발생"인 전복 감지 비율이다.
        </div>
      </div>
    </TypeWrap>
  );
}

const Seg = styled.div`
  display: inline-flex;
  margin-bottom: 4px;
  button {
    font-size: 12px;
    padding: 4px 10px;
    border: 1px solid #d1d5db;
    background: #fff;
    color: #374151;
    cursor: pointer;
    margin-left: -1px;
  }
  button:first-of-type {
    border-radius: 4px 0 0 4px;
    margin-left: 0;
  }
  button:last-of-type {
    border-radius: 0 4px 4px 0;
  }
  button[aria-pressed="true"] {
    background: #25863f;
    border-color: #25863f;
    color: #fff;
    position: relative;
  }
`;

const TypeWrap = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 1fr) 2fr;
  gap: 16px;
  align-items: start;
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
  .sub {
    font-size: 12px;
    color: #6b7280;
  }
  .table {
    overflow-x: auto;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12.5px;
    white-space: nowrap;
  }
  th {
    background: #f3f4f6;
    color: #374151;
    font-weight: 600;
    padding: 7px 8px;
    text-align: right;
    border-bottom: 1px solid #e5e7eb;
  }
  th:first-of-type,
  td.l {
    text-align: left;
  }
  td {
    padding: 6px 8px;
    text-align: right;
    border-bottom: 1px solid #f0f1f3;
    font-variant-numeric: tabular-nums;
  }
  td.dim {
    color: #9ca3af;
  }
  .note {
    margin-top: 6px;
    font-size: 11.5px;
    color: #6b7280;
  }
`;
